// i18n area 'networks' (task #127): sections networks.
// Split out of the old monolithic dictionaries.ts by scripts/split-dictionaries.mjs;
// since the split, these files are the SOURCE — add and edit keys here. The script
// only re-runs to re-split a merged monolithic dictionaries.ts (see its header).

import type { PluralForms } from '../plural'

// Helper locale al file, identico a plural() in ../plural: un import RUNTIME
// da una sezione romperebbe scripts/i18n-status.mjs, che carica questi file
// con node (type stripping, niente resolver TS) — l'import type si cancella,
// uno runtime no. Il tipo resta l'unico e vero contratto.
const plural = (forms: PluralForms): PluralForms => forms

export const enUS = {
  networks: {
    common: {
      name: 'Name',
      scope: 'Scope',
      operState: 'State',
      actions: 'Actions',
      create: 'Create',
      close: 'Close',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      save: 'Save',
      editError: 'Update failed',
      search: 'Search by name',
      empty: 'Nothing here yet',
      saveError: 'Could not save',
      loadError: 'Could not load — is the TVirt backend reachable?',
      createError: 'Creation failed',
      deleteError: 'Deletion failed',
      deleteConfirmTitle: 'Delete {name}?',
      deleteConfirmBody: 'This cannot be undone.',
      nameHint: 'Lowercase letters, digits and dashes (DNS-1123)',
      up: 'Up',
      down: 'Down',
      unknown: 'Unknown',
      usedByVMs: 'VMs',
      emptyTable: 'No entries — add one with the + button'
    },
    dialogTabs: {
      general: 'General',
      base: 'Base definition',
      advanced: 'Advanced configuration',
      reserved: 'TVirt reserved ranges',
      position: 'Position',
      network: 'Network configuration',
      hosts: 'Participating nodes'
    },
    reservedRanges: {
      note: 'These are the IP ranges reserved to TVirt that it may use to automatically assign an address to VMs, containers, etc. NOTE: addresses in these ranges must not be used outside TVirt by external services or appliances.',
      from: 'From',
      to: 'To'
    },
    overlay: {
      title: 'Overlay networks',
      createTitle: 'Create overlay network',
      editTitle: 'Edit overlay network',
      vni: 'VNI',
      vniHint: 'VXLAN Network Identifier — auto-assigned when empty',
      subnet: 'Subnet',
      subnetHint: 'The overlay CIDR — the IPAM derives its registry entry from it automatically',
      mtu: 'MTU',
      weight: 'Weight',
      ceilMbps: 'Ceiling (Mbps)',
      fairnessHint: 'The weight only matters under contention: on an idle link every network may use the full bandwidth. The ceiling is always enforced.',
      contention: 'Contention',
      contentionTip: 'At least one node observes drops or overlimits WITH a backlog on this network\'s class: the uplink bandwidth is contended and the weights are biting. How much and since when live in the node\'s net.shaping.* series.',
      firewallPolicy: 'Firewall policy',
      hosts: 'Hosts',
      hostsTitle: 'Participating nodes',
      hostsHint: 'Each node declares its underlay endpoint — a select over the node\'s REAL addresses, read from its network configuration; the optional overlay address must sit inside the subnet. Participation is the placement set.',
      hostNode: 'Node',
      hostUnderlay: 'Underlay IP',
      hostUnderlayPickNode: 'Pick the node first',
      hostAddress: 'Overlay address (optional)',
      hostAddressOutside: 'Outside the overlay subnet ({cidr})',
      addHost: 'Add node',

      // Round #156 (dialog standard): the observed rows of the details dialog.
      observedAt: 'Observed at',
      attachedNics: 'Attached NICs'
    },
    services: {
      title: 'Network services',
      createTitle: 'Create network service',
      editTitle: 'Edit network service',
      kind: 'Kind',
      kindCategory: 'Services',
      selectKindHint: 'Pick the service kind — the daemon underneath is an implementation detail TVirt manages for you.',
      kindLabel: {
        dhcp: 'DHCP server',
        dns: 'DNS server',
        ntp: 'NTP server',
        vpn: 'VPN server',
        gateway: 'Gateway',
        'lb-rp': 'Load balancer & reverse proxy',
        floatip: 'Floating IP'
      },
      kindDesc: {
        dhcp: 'Hands out addresses on the served subnets — pools derive from the network\'s subnet, gateway and TVirt reserved ranges.',
        dns: 'Authoritative DNS over the declared zones — records derive from the IPAM plus your manual ones.',
        gateway: 'The node acts as gateway for a network — NAT (DNAT, 1:1, SNAT/masquerade) plus static routing between the attached networks, self-healing by design.',
        'lb-rp': 'Load balancer / reverse proxy in front of your services — L4 or L7 listeners over pools of members or over a Pod\'s ready instances, reconfigured in flight.',
        ntp: 'Time service for the networks — relay of external upstream servers, or the node\'s own clock served to the clients.',
        vpn: 'A VPN concentrator on the node — WireGuard or OpenVPN, your choice: remote access for roaming clients, or a site-to-site tunnel between two networks.',
        floatip: 'One address that sits on a single healthy App instance or Pod at a time and moves to the next healthy target when it falls — failover without a daemon; the balancing stays with the load balancer.'
      },

      // Round Pod nativi P4: the floating ip (4b) and the lb-rp Pod backend (4a).
      floatip: {
        hint: 'The floating IP lives on the chosen network, as an ADDITIONAL address inside the holder instance\'s own namespace: no ports, no balancing, no NAT — the client IP stays true.',
        address: 'Floating IP',
        targets: 'Targets',
        targetsHint: 'In order of PREFERENCE: the first healthy target holds the address. A Pod stands for every instance, by replica; each target has its own health criterion. Every target must be attached to the same network.',
        targetKind: 'Target',
        targetApp: 'App instance',
        targetPod: 'Pod (every instance)',
        targetRef: 'Which',
        health: 'Health',
        healthRunning: 'Running (every container ready)',
        healthTcp: 'TCP connect',
        healthHttp: 'HTTP GET',
        healthPing: 'Ping',
        port: 'Port',
        path: 'Path',
        addTarget: 'Add target',
        moveUp: 'Move up',
        moveDown: 'Move down',
        preempt: 'Preempt',
        preemptHint: 'Off (default): a healthy holder keeps the address even when a higher target comes back — no flapping. On: the first healthy target always holds it.',
        holder: 'Holder',
        noHolder: 'no healthy target',
        since: 'since',
        verdicts: 'Targets observed',
        healthy: 'healthy',
        unhealthy: 'unhealthy',
        noVerdict: 'Not decided yet — the authority node writes the first verdict at its next pass.',
        vipChip: 'VIP {address}',
        vipChipTooltip: 'This instance holds the floating IP {address} of the service {service}'
      },
      poolSource: 'Endpoints',
      poolSourceMembers: 'Static members',
      poolSourcePod: 'A Pod\'s ready instances',
      poolBackendPod: 'Pod',
      poolBackendPort: 'Port',
      poolBackendHint: 'The endpoints follow the Pod: an instance joins when it runs with every container ready, leaves at once when it is scaled down or dies. The active check is on by default.',
      poolBackendCount: 'the ready instances of Pod {pod} on port {port}',
      node: 'Node',
      enabled: 'Enabled',
      parked: 'Parked',
      subnets: 'Subnets',
      subnetsHint: 'The IPAM subnets this instance serves — pools derive from CIDR, gateway and the TVirt reserved ranges',
      noSubnets: 'No IPAM subnets yet — declare a fabric logical network (or an overlay subnet) first: the IPAM derives from them',
      iface: 'Listening interface',
      ifaceHint: 'The host interface the service opens its socket on (e.g. a bridge) — required: a network service is self-contained, "every interface" no longer exists',
      dns: 'DNS servers',
      domain: 'Domain',
      detail: 'Serves',
      subnetCount: '{n} subnets',
      zones: 'Zones',
      zonesHint: 'Authoritative only — the daemon never recurses. A records derive from the declared node addresses inside the referenced IPAM subnets; the reverse switch also serves the matching in-addr.arpa zones (PTR). A zone is served by at most one DNS instance fleet-wide. The optional nameserver is the FQDN written in the NS/SOA records instead of the default ns.<zone>; if it falls inside the zone, its A record still derives from the serving node.',
      zoneName: 'Zone name',
      zoneNs: 'Nameserver (NS)',
      zoneSubnets: 'IPAM subnets',
      zoneReverse: 'Reverse (PTR)',
      zoneRecords: 'Manual records',
      zoneRecordsHint: 'One per line: name TYPE value — types A, CNAME, MX, TXT. On a name collision the manual record wins over the derived one.',
      addZone: 'Add zone',

      // lb-rp (wave 3 foundations): listeners over inline pools — engine-less,
      // reconfigured in flight.
      lbListeningHint: 'The instance declares ONE listening network and ONE address — every listener binds it on its own port; a second address is a second service.',
      listeners: 'Listeners',
      listenersHint: 'Each listener binds the instance address on its own port in L4 (TCP passthrough — the bytes are never opened) or L7 (HTTP) mode and forwards to one of the pools below. An address/port pair is served by at most one instance per node. Changes apply in flight — no restart, no dropped connections.',
      listenerName: 'Name',
      listenerMode: 'Mode',
      modeL7: 'L7 — HTTP reverse proxy',
      modeL4: 'L4 — TCP passthrough',
      listenerPort: 'Port',
      listenerPool: 'Pool',
      addListener: 'Add listener',
      pools: 'Pools',
      poolsHint: 'The backends traffic lands on: static members balanced round-robin or by least busy, with an optional active health check that takes sick members out of rotation.',
      poolName: 'Name',
      poolAlgorithm: 'Algorithm',
      algoRoundRobin: 'Round robin',
      algoLeastRequest: 'Least busy',
      poolCheck: 'Health check',
      checkNone: 'None',
      checkTcp: 'TCP connect',
      checkHttp: 'HTTP',
      poolCheckPath: 'Probe path',
      poolInterval: 'Interval (s)',
      poolMembers: 'Members',
      poolMembersHint: 'One per line: address:port, optionally followed by a weight (1-128) — e.g. 10.0.0.21:8080 2',
      addPool: 'Add pool',
      memberCount: '{n} members',

      // ntp (round ntp — ratified 2026-08-03, the last kind of the family):
      // the mode in spoken words, the upstream list only where it means
      // something, the listening attachment reusing the gateway labels.
      // #239 (self-contained netsvc): the shared listener-list editor of the
      // dns and ntp kinds — the two ratified multi-network exceptions.
      listen: {
        title: 'Listeners',
        hint: 'A network service is self-contained: every listener declares its network (overlay, fabric logical network or raw interface) and an address inside its subnet — one listener per address, the wildcard no longer exists. DNS and NTP may listen on several networks and several addresses of those networks.',
        address: 'Listening address',
        assignment: 'Assignment',
        assignmentStatic: 'Static address',
        assignmentAuto: 'Automatic (TVirt reserved ranges)',
        autoHint: "TVirt extracts the address from the logical network's reserved ranges and keeps it sticky — never re-drawn on an edit or a failover.",
        add: 'Add listener'
      },
      ntp: {
        mode: 'Synchronization mode',
        modeRelay: 'Relay of external servers',
        modeNodeClock: 'Node\'s own clock',
        modeHint: 'Relay serves time taken from the declared upstream servers; the node\'s own clock serves the serving node\'s time — which the cluster keeps synchronized anyway',
        servers: 'Upstream servers',
        serversHint: 'Comma separated — hostname or IP address; the service takes the time from here and serves it to the network',
        port: 'Listening port',
        listening: 'Listening on'
      },

      vpn: {
        engine: 'Engine',
        engineHint: 'Your choice, and it never changes: WireGuard is modern, fast and in-kernel; OpenVPN is the compatibility veteran (TCP, restrictive networks)',
        engineLocked: 'The engine is immutable — another engine is a new service, its keys and certificates are not portable',
        mode: 'Mode',
        modeRemote: 'Remote access',
        modeSite: 'Site to site',
        modeHint: 'Remote access hands roaming clients an address in the tunnel; site to site joins two networks',
        port: 'Listening port',
        protocol: 'Transport',
        protocolWireguardHint: 'WireGuard speaks UDP only',
        tunnelSubnet: 'Tunnel subnet',
        tunnelSubnetHint: 'The network the clients live in — registered in the IPAM registry, it lives and dies with this service',
        dnsServers: 'DNS pushed to clients',
        routes: 'Routes pushed to clients',
        routesHint: 'Comma-separated networks reachable through the tunnel; the tunnel subnet is always included',
        peers: 'Peers',
        peersHint: 'Leave the public key empty and TVirt mints the pair — the profile export then hands it over. Paste a key and the peer keeps its own.',
        clients: 'Clients',
        clientsHint: 'Each client gets a certificate issued by this service\'s own CA. Removing a client REVOKES it: the CRL is regenerated at once.',
        peerName: 'Name',
        publicKey: 'Public key',
        publicKeyMinted: 'TVirt mints the pair',
        peerAddress: 'Tunnel address',
        peerAddressHint: 'Empty = assigned',
        allowedIps: 'Remote subnets',
        allowedIpsHint: 'Comma-separated, routed through this peer',
        endpoint: 'Endpoint',
        presharedKey: 'Pre-shared key',
        keepalive: 'Keepalive (s)',
        addPeer: 'Add peer',
        addClient: 'Add client',
        auth: 'Authentication',
        authTls: 'Certificates (TLS)',
        remote: 'Remote endpoint',
        remoteHint: 'host:port of the far end — empty means this side waits',
        peerRoutes: 'Remote subnets',
        serverPublicKey: 'Public key of this server',
        ca: 'Certification authority',
        listening: 'Listening on',
        tunnel: 'Tunnel',
        peerCount: '{n} peers',
        download: 'Download profile',
        downloadError: 'The profile could not be exported',
        lastHandshake: 'Last handshake',
        never: 'never',
        traffic: '↓ {rx} · ↑ {tx}',
        connected: 'Connected',
        idle: 'Idle'
      },

      // Gateway (wave 3 — born nat, ratified 2026-08-01/02; renamed +
      // extended 2026-08-03): the three NAT rule families plus static
      // routes over a listening network. Every ratified form is supported.
      gateway: {
        attach: 'Listening network type',
        attachOverlay: 'Overlay network',
        attachNetwork: 'Fabric logical network',
        attachInterface: 'Raw interface',
        overlay: 'Overlay network',
        network: 'Logical network',
        interface: 'Interface name',
        interfaceHint: 'A host interface TVirt does not manage — the address is taken on trust',
        address: 'Listening address (gateway)',
        addressHint: 'The gateway address clients point at — inside the listening network\'s subnet',
        dnatTitle: 'DNAT rules',
        dnatHint: 'Destination NAT: matched traffic is redirected to a new address (and optionally a new port). Any protocol of the list — ports where the protocol carries them.',
        snatTitle: 'SNAT rules',
        snatHint: 'Source NAT: matched traffic leaves with a rewritten source — an explicit new address (and optionally a new port), or masquerade on the egress address when left empty.',
        oneToOneTitle: '1:1 NAT rules',
        oneToOneHint: 'Every packet for the external address maps to the internal one, and the internal host egresses as the external — all protocols, both directions.',
        external: 'External address',
        internal: 'Internal address',
        addOneToOne: 'Add 1:1 rule',
        family: 'Family',
        protocol: 'Protocol',
        source: 'Source (address/subnet)',
        destination: 'Destination (address/subnet)',
        invert: 'NOT',
        srcPorts: 'Source ports',
        dstPorts: 'Dest. ports',
        dstPortsHint: 'Port or range (80, 8000-8099)',
        toAddress: 'New address',
        toPort: 'New port',
        snatTo: 'New source (empty = masquerade)',
        snatToHint: 'Explicit source address; empty masquerades on the egress address',
        addDnat: 'Add DNAT rule',
        addSnat: 'Add SNAT rule',
        routesTitle: 'Static routes',
        routesHint: 'The node routes between the networks it is attached to (IP forwarding is on); these routes add reachability beyond them — kernel pure, dynamic routing comes with a later round.',
        routeDestination: 'Destination subnet',
        routeGateway: 'Next hop',
        routeMetric: 'Metric',
        addRoute: 'Add route',
        listening: 'Listening on',
        rules: '{d} DNAT · {o} 1:1 · {s} SNAT · {r} routes'
      },
      unknownSince: 'Unknown — last report {m} min ago',
      lastWord: 'Last word',

      // Round #156 (dialog standard): the observed rows and the read-only
      // summary of the kind block in the details dialog.
      reportObservedAt: 'Last report',
      lastErrorRow: 'Last error',
      recordsCount: '{n} manual records',
      phase: {
        configured: 'Configured',
        running: 'Running',
        error: 'Error'
      },

      // Round L (L5, PROPOSAL 15): il DOVE come osservazione — la colonna
      // resta il nodo osservato, l'authority è l'assegnazione durevole, la
      // preferenza è l'unica metà scrivibile (semantica vms.preferredNode).
      authorityNode: 'Authority (assignment)',
      preferredNode: 'Preferred node',
      preferredNodeHint: 'A soft preference, never a constraint: at the next placement decision the instance lands here when this member is eligible — by itself it moves nothing; the move is "Transfer authority".',
      noPreference: 'No preference (automatic placement)',
      moving: 'Moving',
      movingTooltip: 'Observed here while the authority assigns {node}: the move is converging.',

      // Il pannello placement (PROPOSAL 15): il censimento GET .../placement
      // detto APERTO — eleggibili, perché no, SPOF, what-if e il warning
      // «preferenza ignorata» (P-L1c, superficie ratificata a verbale L5).
      placement: {
        title: 'Placement',
        loading: 'Census in progress…',
        loadError: 'The placement census did not answer — retry later.',
        draftHint: 'The census is computed from the declared network attachment: it appears once the service exists. Creation is refused when no member can serve it, and the refusal names the mechanism that emptied the set.',
        eligibleCount: '{n} of {m} members can serve this instance',
        recommended: 'Recommended',
        eligible: 'Eligible',
        notEligible: 'Not eligible',
        blockedReachability: 'the network does not arrive on this member',
        blockedRequireTag: 'blocked by a required tag (requireNodes)',
        blockedExcluded: 'excluded by the simulation',
        preferenceUnknown: 'it is not (or no longer) a cluster member',
        preferenceIgnored: 'The preference "{node}" is not eligible today: {reason}. The census recommendation takes over — never an error, the service lives elsewhere.',
        emptyReachability: 'No member can serve this instance: the network arrives nowhere — no tag edit will help. Cable the node, or move the service where the network is.',
        emptyRequireTag: 'No member can serve this instance: the required tags emptied the set — remove the requireNodes entry, or tag a member.',
        spof: 'Single point of failure',
        spofDetail: 'Exactly one member can host the instance: on failure the failover has nowhere to go. A multi-listener service moves as a UNIT — the candidate must see ALL of its networks: that is the cost.',
        whatIf: 'Simulate members gone',
        whatIfHint: 'The drain what-if: ask BEFORE the evacuation, do not discover it at failover.',
        survives: 'With {nodes} gone the service still has a home.',
        notSurvives: 'Evacuating {nodes} leaves this service DOWN: the derived constraint wins over availability (R30) — a daemon alive where the network does not arrive serves nobody and alarms nobody.',
        transfer: 'Transfer authority',
        transferError: 'Transfer failed',
        transferTarget: 'Target member',
        transferHint: 'Only eligible members are offered — a preference inside the valid set, never an override. No task: the workers\' next pass IS the move (the old node reaps, the new one renders).'
      }
    },
    fabric: {
      title: 'Fabric',
      createTitle: 'Create fabric',
      networks: 'Networks',
      switches: 'Switches',
      ports: 'Ports',
      laggs: 'LAGGs',
      subnets: 'Subnets',
      tabOverview: 'Overview',
      tabNetworks: 'Logical networks',
      tabSwitches: 'Switches',
      tabDerivation: 'Derivation',
      importNetwork: 'Import from another fabric',
      importTitle: 'Import a logical network',
      importHint: "Importing is the explicit adhesion: the network stays the owner's (same identity, same stem, same tvf-<stem> bridge) and from here it is mapped on this fabric's ports/LAGGs — mapping IS asserting that the delivery reaches the same L2 domain as the owner's.",
      importPick: "Owner's network",
      importNone: 'Nothing to import: the other fabrics have no own network this fabric does not already know',
      importedNetworksTitle: 'Imported networks',
      noImported: 'No imported network',
      importedFrom: 'imported from {fabric}',
      importedBy: 'Imported by: {list}',
      importedCount: '+{n} imported',
      owner: 'Owner',
      bridge: 'Bridge',
      bond: 'Bond',
      unimport: 'Remove import',
      unimportConfirm: 'Remove the import of network {name} (owner {fabric}) from this fabric? It is refused while a port or LAGG of this fabric maps it, or a guest, App or service is served only through this leg.',
      unimportRefused: 'Import still in use — {detail}',
      deriv: {
        codeHint: {
          deviceHeldByFabric: 'This node already takes that network from another fabric it is a member of: a node takes a shared network from ONE of its legs (two uplinks of one L2 in a bridge without STP are a loop). Unmap it on the ports or LAGG of one of the two fabrics.',
          otherFabricDerived: "The document carries the block of a fabric the node no longer declares a cable toward: apply that fabric's plan first (it proposes the removal of its devices), or take ownership of them if the fabric is gone."
        },
        hint: 'The O.3 derivation plan: for every cabled node the state, the derived-vs-current diff, the DECLARED bond topology, the dual-stack flips and the structured refusals/warnings. Read-only until the EXPLICIT apply — nothing ever lands on a node by itself.',
        refresh: 'Reload the plan',
        empty: 'No node is cabled to this fabric (and none still carries its block).',
        refusal: 'Refusal',
        warning: 'Warning',
        generation: 'document generation {n}',
        state: { manual: 'Manual', derived: 'Derived', diverged: 'Diverged' },
        topologyTitle: 'Declared topology',
        members: 'members',
        primary: 'primary',
        held: 'held at ONE member',
        form: {
          perNetworkBond: 'Per-network bond',
          singleBond: 'Single fallback bond',
          laggBond: 'LACP bond (LAGG)',
          singlePort: 'Single port'
        },
        colAction: 'Action',
        colKind: 'Kind',
        colDevice: 'Device',
        colDetail: 'Detail',
        action: { add: 'add', remove: 'remove', replace: 'replace' },
        diffEmpty: 'No pending diff — the applied document matches the render.',
        diffBlocked: 'No diff: the refusals above block this node.',
        phasePlanned: 'planned (step 1 of 3)',
        phaseDraining: 'draining (step 2 of 3)',
        probeUnobserved: 'Drain probe: never reported — emptiness UNKNOWN (a retire now would refuse).',
        probeFailed: 'Drain probe: the observation FAILED — the last known timestamps carry over, never an «empty» nobody measured.',
        probeActive: plural({
          one: 'Drain probe: {n} established connection still on the old address',
          other: 'Drain probe: {n} established connections still on the old address'
        }),
        probeLastActive: 'last active {time}',
        probeEmpty: plural({ one: 'Drain probe: empty for {n} minute', other: 'Drain probe: empty for {n} minutes' }),
        probeEmptyUnknown: 'Drain probe: empty (since when is unknown).',
        impact: '{nics} vNIC and {apps} App on this node use these networks: {list}',
        apply: 'Apply to this node',
        applyTitle: 'Apply the derivation — {node}',
        applyIntro:
          'The derived configuration rewrites the network document of {node} ({n} changes) through the same door as the host-network editor — NetworkManager checkpoint and honest probe included. The plan warnings below ride along: read them before confirming.',
        applyError: 'Apply failed',
        applyRefused: 'The node refused:',
        planMoved: 'The fabric changed after this preview — NOTHING was applied. The plan has been reloaded: look again and retry.',
        retire: 'Retire',
        retireTitle: 'Point of no return — retiring {address}',
        restore: 'Restore',
        restoreTip: 'Back to the pre-apply snapshot (generation {previous}) — the one-step undo (H13)',
        restoreTitle: 'Restore the previous document — {node}',
        restoreIntro: 'The network document of {node} goes back to the snapshot taken before the last apply (generation {previous}), rewritten as generation {next} in place of {current}. The worker converges it like any other document.',
        restoreRedo: 'The replaced document becomes the new snapshot: restoring again is the redo.',
        restoreClosesChain: 'The snapshot is the pre-derivation manual document: the chain closes here — no further undo, the node reads manual until the next apply.',
        restoreConfirm: 'Restore',
        restoreError: 'Restore failed',
        retireIntro:
          'This removes {address} ({network}) from {point} for good. After this act the way back is the INVERSE flip — re-declaring the old address through the same engine — never a blind restore.',
        retireOld: 'Old address (removed)',
        retireNew: 'New address (stays)',
        retireEmptyFact: 'The drain probe reports the address empty: no observed connection is severed by this removal.',
        retireConfirm: 'Retire the address',
        severActiveLoss: plural({
          one: 'Sever {n} established connection still using the old address — it dies with this act',
          other: 'Sever {n} established connections still using the old address — they die with this act'
        }),
        severUnknownLoss: 'Accept the UNKNOWN: the probe never observed this address (or the observation fails) — live connections may be severed.'
      },
      overviewHint: 'The fabric at a glance: every logical network with its MTU and subnet (VLANs live on the per-port/per-LAGG mappings only — the same network may ride different tags on different ports). The fabric is a registry of EXTERNAL infrastructure — TVirt observes and declares, never provisions, and only ever touches the NICs registered under its control.',
      absorptionHint: 'Every subnet declared here DERIVES its IPAM registry entry in the same write — it lives and dies with the network.',
      scopeFixedHint: 'The scope is fixed at the cluster root: physical infrastructure is cluster-wide by definition.',
      networksHint: 'Every network carries a mandatory MTU (default 1500) and a mandatory subnet; gateway, DNS servers and static routes declared here are the SINGLE SOURCE for server NICs, Apps and network services.',
      switchesHint: 'The manual registry: a port is unused, mapped (untagged and per-VLAN-tag traffic onto the logical networks) or a LAGG member — a member carries no mappings of its own.',
      mtu: 'MTU',
      cidr: 'CIDR',
      cidrHint: 'Mandatory — the IPAM derives its registry entry automatically',
      nodeAddressing: 'Node addressing',
      nodeAddressingNone: 'None — no per-node address',
      nodeAddressingPool: 'Pool — one sticky address per delivered node',
      nodeAddressingHint: 'With Pool, the fabric derivation assigns each delivered node an address from the ranges reserved to TVirt (sticky once assigned).',
      backing: 'Backing',
      gateway: 'Gateway',
      ranges: 'Reserved ranges',
      dns: 'DNS servers (comma-separated)',
      dnsShort: 'DNS',
      singleSourceHint: 'Gateway, DNS and routes declared here are the single source — server NICs, Apps and network services draw from them',
      routesTitle: 'Static routes',
      routeDestination: 'Destination (CIDR)',
      routeVia: 'Via (next hop)',
      portUsed: 'Used',
      vlanMappings: 'VLAN mappings',
      vlanTag: 'VLAN tag',
      vlanNetwork: 'Logical network',
      nameTaken: 'This name is already used in the fabric',
      addNetwork: 'Add network',
      editNetwork: 'Edit network',
      deleteNetwork: 'Remove network',
      deleteNetworkConfirm: 'Remove logical network {name} from the fabric? Its derived IPAM subnet dies with it (refused while a service or App still uses it). Port and LAGG mappings pointing at it are cleared.',
      switchName: 'Switch',
      portName: 'Port',
      positionHint: 'Physical position (optional) — the same vocabulary as the server placement; declarative only',
      addSwitch: 'Add switch',
      editSwitch: 'Edit switch',
      deleteSwitch: 'Remove switch',
      deleteSwitchConfirm: 'Remove switch {name} with all its ports? LAGGs shrink accordingly (and die under two ports).',
      addPort: 'Add port',
      editPort: 'Edit port',
      deletePort: 'Remove port',
      deletePortConfirm: 'Remove port {name}?',
      untagged: 'Untagged',
      tagged: 'Tagged (tag:network)',
      taggedShort: 'Tagged',
      portCabling: 'Cabling',
      portState: 'State',
      stateUnused: 'unused',
      stateMapped: 'mapped',
      laggPortGuard: 'A LAGG member is edited from the LAGG side',
      laggsTitle: 'LAGGs',
      laggsHint: 'A LAGG is 2+ ports in LACP and may span switches (MLAG, transparent); only unused ports join, the LAGG is the mapping surface, a removed port returns to unused.',
      addLagg: 'Add LAGG',
      editLagg: 'Edit LAGG',
      deleteLagg: 'Remove LAGG',
      deleteLaggConfirm: 'Remove LAGG {name}? Its member ports return to unused.',
      lacpRate: 'LACP rate',
      laggPorts: 'Member ports (2+)',
      laggPortsHint: 'Only unused ports can join — clear a mapped port first',
      noNetworks: 'No logical networks declared yet.',
      noSwitches: 'No switches declared yet.',
      noLaggs: 'No LAGGs declared.',
      staleConflict: 'Someone else changed this fabric while you were editing it: the save was refused so their work is not wiped out. Your changes are still here, untouched. The updated fabric has already been reloaded: confirm again to re-apply them on top, or cancel and look at what changed.'
    },
    ipam: {
      title: 'IPAM',
      derivedHint: 'Derived only: every subnet comes from a fabric logical network or an overlay — edit it at its source. Assigned addresses show WHO holds them; the TVirt reserved ranges are the automatic-assignment pools.',
      search: 'Search by name, CIDR or address',
      empty: 'No networks yet — declare a fabric logical network or an overlay subnet: the IPAM derives from them automatically.',
      reserved: 'TVirt reserved ranges',
      address: 'Address',
      origin: 'Origin',
      origin_declared: 'declared',
      origin_observed: 'observed',
      holder: 'Held by',
      noAddresses: 'No addresses known inside this subnet yet.',
      sourceFabric: 'Fabric',
      sourceOverlay: 'Overlay',
      editAtSource: 'Edit at the source'
    },

    // Rete > Firewall (#168): il vocabolario dei gruppi + la terna per-NIC.
    firewall: {
      title: 'Firewall groups',
      createTitle: 'New firewall group',
      editTitle: 'Edit firewall group',
      empty: 'No firewall groups yet — create one: a reusable rule vocabulary, referenced from guest NICs by name.',
      nameHint: 'Letters, digits and interior . - _; unique within its scope (homonyms elsewhere are told apart by /scope/name)',
      description: 'Description',
      descriptionHint: 'One line of intent, shown wherever the group is picked',
      enforced: 'Enforced on the scope',
      enforcedHint: 'Off: pure vocabulary — the rules bind only the NICs that reference the group. On: the rules bind every guest NIC under the group\'s scope whose firewall is on, referenced or not.',
      enforcedChip: 'Enforced',
      enforcedChipHint: 'The rules bind every guest NIC under the group\'s scope whose firewall is on, referenced or not',
      enforceWarning: 'Raising enforcement binds the rules to EVERY guest NIC under {scope} whose firewall is on at the next convergence, even without an explicit reference — and inside an active chain an ancestor\'s drop is final: the levels below cannot reopen it.',
      unenforceInfo: 'Dropping enforcement leaves the rules only on the NICs that reference the group explicitly.',
      rules: 'Rules',
      tabOverview: 'Overview',
      tabRules: 'Rules',
      orderHint: 'Row order IS the evaluation order within the group: the first matching rule wins.',
      statefulHint: 'Stateful is implicit: replies to admitted traffic pass on their own (established/related accepted, invalid dropped — injected by the engine, never written by hand).',
      noRules: 'No rules — a vocabulary under construction: referencing it is legal and renders nothing.',
      addRule: 'Add rule',
      direction: 'Direction',
      directionIn: 'Inbound',
      directionOut: 'Outbound',
      action: 'Action',
      action_accept: 'Accept',
      action_drop: 'Drop',
      action_reject: 'Reject',
      protocol: 'Protocol',
      protocolAny: 'Any',
      ports: 'Ports',
      source: 'Source',
      dest: 'Destination',
      comment: 'Comment',
      addressHint: 'IPv4 CIDR or <fabric>/<logical network>; empty = any',
      moveUp: 'Move up',
      moveDown: 'Move down',
      nicFirewall: 'Firewall',
      nicAntiSpoof: 'Anti-spoofing',
      nicGroups: 'Firewall groups',
      nicGroupsPlaceholder: 'Pick a group…',
      nicGroupsHint: 'The visible groups, ancestors included (usable downward, like tags)',
      nicGroupCount: plural({ one: '{n} firewall group', other: '{n} firewall groups' }),
      nicOff: 'Firewall off',
      pendingRestartHint: 'Firewall changes follow the saved/applied model: they wait for the next power cycle (PendingRestart). Groups enforced by ancestor scopes apply whenever the NIC\'s firewall is on.',
      appConvergeHint: 'Firewall changes apply at the container\'s next convergence. Groups enforced by ancestor scopes apply whenever the firewall is on.',
      enforcedAncestors: 'Enforced by ancestor scopes',
      enforcedAncestorsHint: 'This group applies to every NIC under its scope whose firewall is ON — the toggle is the NIC\'s master switch (off = no chain at all, ancestors included); inside an active chain the ancestor\'s authority is final: its drop is not reopened below.'
    }
  }
}

export const itIT: typeof enUS = {
  networks: {
    common: {
      name: 'Nome',
      scope: 'Scope',
      operState: 'Stato',
      actions: 'Azioni',
      create: 'Crea',
      close: 'Chiudi',
      cancel: 'Annulla',
      delete: 'Elimina',
      edit: 'Modifica',
      save: 'Salva',
      editError: 'Modifica non riuscita',
      search: 'Cerca per nome',
      empty: 'Ancora niente qui',
      saveError: 'Impossibile salvare',
      loadError: 'Impossibile caricare — il backend TVirt è raggiungibile?',
      createError: 'Creazione non riuscita',
      deleteError: 'Eliminazione non riuscita',
      deleteConfirmTitle: 'Eliminare {name}?',
      deleteConfirmBody: 'L\'operazione non è reversibile.',
      nameHint: 'Minuscole, cifre e trattini (DNS-1123)',
      up: 'Attiva',
      down: 'Non attiva',
      unknown: 'Sconosciuto',
      usedByVMs: 'VM',
      emptyTable: 'Nessuna voce — aggiungine una col pulsante +'
    },
    dialogTabs: {
      general: 'Generale',
      base: 'Definizione base',
      advanced: 'Configurazione avanzata',
      reserved: 'Range riservati a TVirt',
      position: 'Posizione',
      network: 'Configurazione di rete',
      hosts: 'Nodi partecipanti'
    },
    reservedRanges: {
      note: 'Questi sono i range di IP riservati a TVirt che può usare per assegnare automaticamente un indirizzo a VM, container, ecc. NOTA: gli IP in questi range non devono essere usati al di fuori di TVirt da servizi/apparati esterni.',
      from: 'Da',
      to: 'A'
    },
    overlay: {
      title: 'Reti overlay',
      createTitle: 'Crea rete overlay',
      editTitle: 'Modifica rete overlay',
      vni: 'VNI',
      vniHint: 'VXLAN Network Identifier — assegnato automaticamente se vuoto',
      subnet: 'Subnet',
      subnetHint: "Il CIDR della overlay — l'IPAM ne deriva automaticamente la voce di registro",
      mtu: 'MTU',
      weight: 'Peso',
      ceilMbps: 'Tetto (Mbps)',
      fairnessHint: 'Il peso vale solo sotto contention: a link scarico ogni rete può usare tutta la banda. Il tetto invece è sempre attivo.',
      contention: 'Contesa',
      contentionTip: 'Almeno un nodo osserva scarti o overlimit CON coda sulla classe di questa rete: la banda dell\'uplink è contesa e i pesi stanno mordendo. Il quanto e il da quando sono nelle serie net.shaping.* del nodo.',
      firewallPolicy: 'Politica firewall',
      hosts: 'Host',
      hostsTitle: 'Nodi partecipanti',
      hostsHint: "Ogni nodo dichiara il suo endpoint underlay — una select sugli indirizzi REALI del nodo, letti dalla sua configurazione di rete; l'indirizzo overlay opzionale deve stare nella subnet. La partecipazione è il vincolo di placement.",
      hostNode: 'Nodo',
      hostUnderlay: 'IP underlay',
      hostUnderlayPickNode: 'Scegli prima il nodo',
      hostAddress: 'Indirizzo sulla overlay (opzionale)',
      hostAddressOutside: 'Fuori dalla subnet dell\'overlay ({cidr})',
      addHost: 'Aggiungi nodo',

      // Round #156 (dialog standard): le righe osservate del dialog dettagli.
      observedAt: 'Osservato il',
      attachedNics: 'NIC collegate'
    },
    services: {
      title: 'Servizi di rete',
      createTitle: 'Crea servizio di rete',
      editTitle: 'Modifica servizio di rete',
      kind: 'Tipo',
      kindCategory: 'Servizi',
      selectKindHint: 'Scegli il tipo di servizio — il demone sottostante è un dettaglio di implementazione che TVirt gestisce per te.',
      kindLabel: {
        dhcp: 'Server DHCP',
        ntp: 'Server NTP',
        vpn: 'Server VPN',
        dns: 'Server DNS',
        gateway: 'Gateway',
        'lb-rp': 'Load balancer e reverse proxy',
        floatip: 'IP flottante'
      },
      kindDesc: {
        dhcp: 'Assegna indirizzi sulle subnet servite — i pool derivano da subnet, gateway e range riservati a TVirt della rete.',
        dns: 'DNS autoritativo sulle zone dichiarate — i record derivano dall\'IPAM più i tuoi manuali.',
        gateway: 'Il nodo fa da gateway per una rete — NAT (DNAT, 1:1, SNAT/masquerade) più routing statico tra le reti attaccate, auto-riparante per design.',
        'lb-rp': 'Bilanciatore / reverse proxy davanti ai tuoi servizi — listener L4 o L7 su pool di membri o sulle istanze pronte di un Pod, riconfigurati in volo.',
        ntp: 'Il servizio orario delle reti — relay di server upstream esterni, oppure l\'orologio del nodo servito ai client.',
        vpn: 'Un concentratore VPN sul nodo — WireGuard oppure OpenVPN, scegli tu: accesso remoto per i client itineranti, o un tunnel site to site fra due reti.',
        floatip: 'Un indirizzo che sta su UNA sola istanza App o Pod sana alla volta e si sposta sul prossimo bersaglio sano quando quello cade — failover senza demoni; il bilanciamento resta al load balancer.'
      },

      // Round Pod nativi P4: l'IP flottante (4b) e il backend Pod dell'lb-rp (4a).
      floatip: {
        hint: 'L\'IP flottante vive sulla rete scelta, come indirizzo IN PIÙ dentro il namespace dell\'istanza detentrice: niente porte, niente bilanciamento, niente NAT — l\'IP del client resta vero.',
        address: 'IP flottante',
        targets: 'Bersagli',
        targetsHint: 'In ordine di PREFERENZA: il primo bersaglio sano tiene l\'indirizzo. Un Pod vale per tutte le sue istanze, per replica; ogni bersaglio ha il suo criterio di salute. Ogni bersaglio deve essere attaccato alla stessa rete.',
        targetKind: 'Bersaglio',
        targetApp: 'Istanza App',
        targetPod: 'Pod (tutte le istanze)',
        targetRef: 'Quale',
        health: 'Salute',
        healthRunning: 'In esecuzione (ogni container pronto)',
        healthTcp: 'Connessione TCP',
        healthHttp: 'HTTP GET',
        healthPing: 'Ping',
        port: 'Porta',
        path: 'Path',
        addTarget: 'Aggiungi bersaglio',
        moveUp: 'Sposta su',
        moveDown: 'Sposta giù',
        preempt: 'Preempt',
        preemptHint: 'Spento (default): un detentore sano tiene l\'indirizzo anche quando torna un bersaglio più in alto — niente flapping. Acceso: il primo bersaglio sano lo tiene sempre.',
        holder: 'Detentore',
        noHolder: 'nessun bersaglio sano',
        since: 'da',
        verdicts: 'Bersagli osservati',
        healthy: 'sano',
        unhealthy: 'non sano',
        noVerdict: 'Non ancora deciso — il nodo autorità scrive il primo verdetto alla prossima passata.',
        vipChip: 'VIP {address}',
        vipChipTooltip: 'Questa istanza tiene l\'IP flottante {address} del servizio {service}'
      },
      poolSource: 'Endpoint',
      poolSourceMembers: 'Membri statici',
      poolSourcePod: 'Le istanze pronte di un Pod',
      poolBackendPod: 'Pod',
      poolBackendPort: 'Porta',
      poolBackendHint: 'Gli endpoint seguono il Pod: un\'istanza entra quando è in esecuzione con ogni container pronto, esce subito quando viene ridotta o muore. L\'health check attivo è acceso di default.',
      poolBackendCount: 'le istanze pronte del Pod {pod} sulla porta {port}',
      node: 'Nodo',
      enabled: 'Abilitato',
      parked: 'Parcheggiato',
      subnets: 'Subnet',
      subnetsHint: 'Le subnet IPAM servite da questa istanza — i pool derivano da CIDR, gateway e range riservati a TVirt',
      noSubnets: "Nessuna subnet IPAM — dichiara prima una rete logica di fabric (o una subnet overlay): l'IPAM deriva da loro",
      iface: 'Interfaccia di ascolto',
      ifaceHint: 'L\'interfaccia host su cui il servizio apre il suo socket (es. un bridge) — obbligatoria: un servizio di rete è autocontenuto, "tutte le interfacce" non esiste più',
      dns: 'Server DNS',
      domain: 'Dominio',
      detail: 'Serve',
      subnetCount: '{n} subnet',
      zones: 'Zone',
      zonesHint: 'Solo autoritativo — il demone non ricorre mai. I record A derivano dagli indirizzi dichiarati dei nodi nelle subnet IPAM referenziate; lo switch reverse serve anche le zone in-addr.arpa corrispondenti (PTR). Una zona è servita da al più una istanza DNS nella flotta. Il nameserver opzionale è l’FQDN scritto nei record NS/SOA al posto del default ns.<zona>; se cade dentro la zona, il suo record A deriva comunque dal nodo servente.',
      zoneName: 'Nome zona',
      zoneNs: 'Nameserver (NS)',
      zoneSubnets: 'Subnet IPAM',
      zoneReverse: 'Reverse (PTR)',
      zoneRecords: 'Record manuali',
      zoneRecordsHint: 'Uno per riga: nome TIPO valore — tipi A, CNAME, MX, TXT. In caso di collisione sul nome vince il record manuale su quello derivato.',
      addZone: 'Aggiungi zona',

      // lb-rp (wave 3 fondamenta): listener su pool inline — engine-less,
      // riconfigurato in volo.
      lbListeningHint: 'L\'istanza dichiara UNA rete di ascolto e UN indirizzo — ogni listener lo lega sulla propria porta; un secondo indirizzo è un secondo servizio.',
      listeners: 'Listener',
      listenersHint: 'Ogni listener lega l\'indirizzo dell\'istanza sulla propria porta in modalità L4 (passthrough TCP — i byte non si aprono mai) o L7 (HTTP) e inoltra a uno dei pool qui sotto. Una coppia indirizzo/porta è servita da al più una istanza per nodo. Le modifiche si applicano in volo — nessun riavvio, nessuna connessione persa.',
      listenerName: 'Nome',
      listenerMode: 'Modalità',
      modeL7: 'L7 — reverse proxy HTTP',
      modeL4: 'L4 — passthrough TCP',
      listenerPort: 'Porta',
      listenerPool: 'Pool',
      addListener: 'Aggiungi listener',
      pools: 'Pool',
      poolsHint: 'I backend su cui atterra il traffico: membri statici bilanciati round-robin o per meno occupato, con un health check attivo opzionale che toglie dalla rotazione i membri malati.',
      poolName: 'Nome',
      poolAlgorithm: 'Algoritmo',
      algoRoundRobin: 'Round robin',
      algoLeastRequest: 'Meno occupato',
      poolCheck: 'Health check',
      checkNone: 'Nessuno',
      checkTcp: 'Connessione TCP',
      checkHttp: 'HTTP',
      poolCheckPath: 'Percorso sonda',
      poolInterval: 'Intervallo (s)',
      poolMembers: 'Membri',
      poolMembersHint: 'Uno per riga: indirizzo:porta, opzionalmente seguito da un peso (1-128) — es. 10.0.0.21:8080 2',
      addPool: 'Aggiungi pool',
      memberCount: '{n} membri',

      // ntp (round ntp — ratifica 2026-08-03, l'ultimo kind della famiglia):
      // il modo in parole parlate, la lista upstream solo dove significa
      // qualcosa, l'attacco di rete riusa le etichette del gateway.
      // #239 (netsvc autocontenuti): l'editor condiviso della lista di
      // listener dei kind dns e ntp — le due eccezioni multi-rete ratificate.
      listen: {
        title: 'Listener',
        hint: "Un servizio di rete è autocontenuto: ogni listener dichiara la sua rete (overlay, rete logica di fabric o interfaccia) e un indirizzo dentro la sua subnet — un listener per indirizzo, il wildcard non esiste più. DNS e NTP possono ascoltare su più reti e più indirizzi di quelle reti.",
        address: 'Indirizzo di ascolto',
        assignment: 'Assegnazione',
        assignmentStatic: 'Indirizzo statico',
        assignmentAuto: 'Automatica (range riservati a TVirt)',
        autoHint: "TVirt estrae l'indirizzo dai range riservati della rete logica e lo tiene sticky — mai riestratto a un edit o a un failover.",
        add: 'Aggiungi listener'
      },
      ntp: {
        mode: 'Modo di sincronizzazione',
        modeRelay: 'Relay di server esterni',
        modeNodeClock: 'Orologio del nodo',
        modeHint: "Il relay serve l'ora presa dai server upstream dichiarati; l'orologio del nodo serve l'ora del nodo servente — che il cluster tiene comunque sincronizzata",
        servers: 'Server upstream',
        serversHint: "Separati da virgola — hostname o indirizzo IP; il servizio prende l'ora da qui e la serve alla rete",
        port: 'Porta di ascolto',
        listening: 'In ascolto su'
      },

      vpn: {
        engine: 'Motore',
        engineHint: 'Lo scegli tu, e non cambia più: WireGuard è moderno, veloce e in-kernel; OpenVPN è il veterano della compatibilità (TCP, reti ostili)',
        engineLocked: 'Il motore è immutabile — un altro motore è un servizio nuovo, chiavi e certificati non sono portabili',
        mode: 'Modo',
        modeRemote: 'Accesso remoto',
        modeSite: 'Site to site',
        modeHint: "L'accesso remoto dà ai client itineranti un indirizzo nel tunnel; il site to site unisce due reti",
        port: 'Porta di ascolto',
        protocol: 'Trasporto',
        protocolWireguardHint: 'WireGuard parla solo UDP',
        tunnelSubnet: 'Subnet del tunnel',
        tunnelSubnetHint: "La rete in cui vivono i client — registrata nel registro IPAM, nasce e muore con questo servizio",
        dnsServers: 'DNS spinti ai client',
        routes: 'Rotte spinte ai client',
        routesHint: 'Reti separate da virgola raggiungibili nel tunnel; la subnet del tunnel è sempre inclusa',
        peers: 'Peer',
        peersHint: 'Lascia vuota la chiave pubblica e TVirt genera la coppia — poi la consegna con il profilo. Incolla una chiave e il peer tiene la sua.',
        clients: 'Client',
        clientsHint: 'Ogni client riceve un certificato emesso dalla CA di questo servizio. Togliere un client lo REVOCA: la CRL si rigenera subito.',
        peerName: 'Nome',
        publicKey: 'Chiave pubblica',
        publicKeyMinted: 'La genera TVirt',
        peerAddress: 'Indirizzo nel tunnel',
        peerAddressHint: 'Vuoto = assegnato',
        allowedIps: 'Subnet remote',
        allowedIpsHint: 'Separate da virgola, instradate attraverso questo peer',
        endpoint: 'Endpoint',
        presharedKey: 'Chiave pre-condivisa',
        keepalive: 'Keepalive (s)',
        addPeer: 'Aggiungi peer',
        addClient: 'Aggiungi client',
        auth: 'Autenticazione',
        authTls: 'Certificati (TLS)',
        remote: 'Endpoint remoto',
        remoteHint: 'host:porta del capo remoto — vuoto significa che questo lato aspetta',
        peerRoutes: 'Subnet remote',
        serverPublicKey: 'Chiave pubblica di questo server',
        ca: 'Autorità di certificazione',
        listening: 'In ascolto su',
        tunnel: 'Tunnel',
        peerCount: '{n} peer',
        download: 'Scarica profilo',
        downloadError: 'Non è stato possibile esportare il profilo',
        lastHandshake: 'Ultimo handshake',
        never: 'mai',
        traffic: '↓ {rx} · ↑ {tx}',
        connected: 'Connesso',
        idle: 'Inattivo'
      },

      // Gateway (wave 3 — nato nat, ratifica 2026-08-01/02; rinominato ed
      // esteso il 2026-08-03): le tre famiglie di regole NAT più le rotte
      // statiche su una rete di listening. Ogni forma ratificata è supportata.
      gateway: {
        attach: 'Tipo di rete di listening',
        attachOverlay: 'Rete overlay',
        attachNetwork: 'Rete logica di fabric',
        attachInterface: 'Interfaccia grezza',
        overlay: 'Rete overlay',
        network: 'Rete logica',
        interface: 'Nome interfaccia',
        interfaceHint: 'Un\'interfaccia host che TVirt non gestisce — l\'indirizzo è preso per buono',
        address: 'Indirizzo di listening (gateway)',
        addressHint: 'L\'indirizzo gateway a cui puntano i client — dentro la subnet della rete di listening',
        dnatTitle: 'Regole DNAT',
        dnatHint: 'NAT di destinazione: il traffico nel match è rediretto a un nuovo indirizzo (e opzionalmente una nuova porta). Qualsiasi protocollo della lista — le porte dove il protocollo le porta.',
        snatTitle: 'Regole SNAT',
        snatHint: 'NAT di sorgente: il traffico nel match esce con la sorgente riscritta — un nuovo indirizzo esplicito (e opzionalmente una nuova porta), o masquerade sull\'indirizzo di uscita se lasciato vuoto.',
        oneToOneTitle: 'Regole 1:1 NAT',
        oneToOneHint: 'Ogni pacchetto per l\'indirizzo esterno mappa sull\'interno, e l\'host interno esce come l\'esterno — tutti i protocolli, entrambe le direzioni.',
        external: 'Indirizzo esterno',
        internal: 'Indirizzo interno',
        addOneToOne: 'Aggiungi regola 1:1',
        family: 'Famiglia',
        protocol: 'Protocollo',
        source: 'Sorgente (indirizzo/subnet)',
        destination: 'Destinazione (indirizzo/subnet)',
        invert: 'NOT',
        srcPorts: 'Porte sorgente',
        dstPorts: 'Porte dest.',
        dstPortsHint: 'Porta o range (80, 8000-8099)',
        toAddress: 'Nuovo indirizzo',
        toPort: 'Nuova porta',
        snatTo: 'Nuova sorgente (vuota = masquerade)',
        snatToHint: 'Indirizzo sorgente esplicito; vuoto = masquerade sull\'indirizzo di uscita',
        addDnat: 'Aggiungi regola DNAT',
        addSnat: 'Aggiungi regola SNAT',
        routesTitle: 'Rotte statiche',
        routesHint: 'Il nodo instrada tra le reti a cui è attaccato (l\'IP forwarding è attivo); queste rotte aggiungono raggiungibilità oltre quelle — kernel puro, il routing dinamico arriva con un round successivo.',
        routeDestination: 'Subnet di destinazione',
        routeGateway: 'Next hop',
        routeMetric: 'Metrica',
        addRoute: 'Aggiungi rotta',
        listening: 'In ascolto su',
        rules: '{d} DNAT · {o} 1:1 · {s} SNAT · {r} rotte'
      },
      unknownSince: 'Sconosciuto — ultimo report {m} min fa',
      lastWord: 'Ultima parola',

      // Round #156 (dialog standard): le righe osservate e il riepilogo in
      // sola lettura del blocco kind nel dialog dettagli.
      reportObservedAt: 'Ultimo report',
      lastErrorRow: 'Ultimo errore',
      recordsCount: '{n} record manuali',
      phase: {
        configured: 'Configurato',
        running: 'In esecuzione',
        error: 'Errore'
      },

      // Round L (L5, PROPOSAL 15): il DOVE come osservazione — la colonna
      // resta il nodo osservato, l'authority è l'assegnazione durevole, la
      // preferenza è l'unica metà scrivibile (semantica vms.preferredNode).
      authorityNode: 'Authority (assegnazione)',
      preferredNode: 'Nodo preferito',
      preferredNodeHint: 'Una preferenza morbida, mai un vincolo: alla prossima decisione di piazzamento l\'istanza atterra qui se il membro è eleggibile — da sola non muove nulla; la mossa è «Trasferisci authority».',
      noPreference: 'Nessuna preferenza (placement automatico)',
      moving: 'In movimento',
      movingTooltip: 'Osservato qui mentre l\'authority assegna {node}: la mossa sta convergendo.',

      // Il pannello placement (PROPOSAL 15): il censimento GET .../placement
      // detto APERTO — eleggibili, perché no, SPOF, what-if e il warning
      // «preferenza ignorata» (P-L1c, superficie ratificata a verbale L5).
      placement: {
        title: 'Piazzamento',
        loading: 'Censimento in corso…',
        loadError: 'Il censimento di piazzamento non ha risposto — riprova più tardi.',
        draftHint: 'Il censimento si calcola sull\'attacco di rete dichiarato: comparirà a servizio creato. La creazione è rifiutata se nessun membro può servirlo, e il rifiuto nomina il meccanismo che ha svuotato l\'insieme.',
        eligibleCount: '{n} membri su {m} possono servire questa istanza',
        recommended: 'Consigliato',
        eligible: 'Eleggibile',
        notEligible: 'Non eleggibile',
        blockedReachability: 'la rete non arriva su questo membro',
        blockedRequireTag: 'bloccato da un tag richiesto (requireNodes)',
        blockedExcluded: 'escluso dalla simulazione',
        preferenceUnknown: 'non è (più) un membro del cluster',
        preferenceIgnored: 'La preferenza «{node}» oggi non è eleggibile: {reason}. Prevale la raccomandazione del censimento — mai un errore, il servizio vive altrove.',
        emptyReachability: 'Nessun membro può servire questa istanza: la rete non arriva da nessuna parte — nessun ritocco ai tag aiuterà. Cabla il nodo, o sposta il servizio dove la rete c\'è.',
        emptyRequireTag: 'Nessun membro può servire questa istanza: sono i tag richiesti ad aver svuotato l\'insieme — togli la voce da requireNodes, o tagga un membro.',
        spof: 'Punto singolo di guasto',
        spofDetail: 'Un solo membro può ospitare l\'istanza: al guasto il failover non ha dove andare. Un servizio multi-listener si sposta come UNITÀ — il candidato deve vedere TUTTE le sue reti: questo è il costo.',
        whatIf: 'Simula membri assenti',
        whatIfHint: 'Il what-if del drain: chiedi PRIMA dell\'evacuazione, non scoprirlo al failover.',
        survives: 'Con {nodes} assente/i il servizio ha ancora una casa.',
        notSurvives: 'Evacuare {nodes} lascia questo servizio GIÙ: il vincolo derivato vince sulla disponibilità (R30) — un demone vivo dove la rete non arriva non serve nessuno e non allarma nessuno.',
        transfer: 'Trasferisci authority',
        transferError: 'Trasferimento non riuscito',
        transferTarget: 'Membro di destinazione',
        transferHint: 'Solo i membri eleggibili sono proposti — una preferenza dentro l\'insieme valido, mai un override. Nessun task: il prossimo passo dei worker È la mossa (il vecchio nodo miete, il nuovo rende).'
      }
    },
    fabric: {
      title: 'Fabric',
      createTitle: 'Crea fabric',
      networks: 'Reti',
      switches: 'Switch',
      ports: 'Porte',
      laggs: 'LAGG',
      subnets: 'Subnet',
      tabOverview: 'Panoramica',
      tabNetworks: 'Reti logiche',
      tabSwitches: 'Switch',
      tabDerivation: 'Derivazione',
      importNetwork: 'Importa da un altro fabric',
      importTitle: 'Importa una rete logica',
      importHint: "L'import è l'adesione esplicita: la rete resta del titolare (stessa identità, stesso stem, stesso bridge tvf-<stem>) e da qui si mappa sulle porte/LAGG di questo fabric — mappare È asserire che la consegna sbocca nello stesso dominio L2 del titolare.",
      importPick: 'Rete del titolare',
      importNone: 'Nessuna rete importabile: gli altri fabric non hanno reti proprie che questo fabric non conosca già',
      importedNetworksTitle: 'Reti importate',
      noImported: 'Nessuna rete importata',
      importedFrom: 'importata da {fabric}',
      importedBy: 'Importata da: {list}',
      importedCount: '+{n} importate',
      owner: 'Titolare',
      bridge: 'Bridge',
      bond: 'Bond',
      unimport: "Rimuovi l'import",
      unimportConfirm: "Rimuovere l'import della rete {name} (titolare {fabric}) da questo fabric? Viene rifiutato finché una porta o un LAGG di questo fabric la mappa, o un guest, un'App o un servizio è servito solo attraverso questa gamba.",
      unimportRefused: 'Import ancora in uso — {detail}',
      deriv: {
        codeHint: {
          deviceHeldByFabric: 'Questo nodo prende quella rete già da un altro fabric di cui è membro: un nodo prende una rete condivisa da UNA sola delle sue gambe (due uplink dello stesso L2 in un bridge senza STP sono un loop). Smappala sulle porte o sul LAGG di uno dei due fabric.',
          otherFabricDerived: 'Il documento porta il blocco di un fabric verso cui il nodo non dichiara più alcun cavo: applica prima il piano di quel fabric (propone la rimozione dei suoi device), oppure prendine possesso con take-ownership se il fabric non esiste più.'
        },
        hint: "Il piano di derivazione O.3: per ogni nodo cablato lo stato, il diff derivata-vs-corrente, la topologia bond DICHIARATA, i flip dual-stack e i rifiuti/avvisi strutturati. Sola lettura fino all'apply ESPLICITO — niente atterra mai su un nodo da solo.",
        refresh: 'Ricarica il piano',
        empty: 'Nessun nodo è cablato a questa fabric (e nessuno ne porta ancora il blocco).',
        refusal: 'Rifiuto',
        warning: 'Avviso',
        generation: 'generazione documento {n}',
        state: { manual: 'Manuale', derived: 'Derivata', diverged: 'Divergente' },
        topologyTitle: 'Topologia dichiarata',
        members: 'membri',
        primary: 'primaria',
        held: 'ferma a UN membro',
        form: {
          perNetworkBond: 'Bond per rete',
          singleBond: 'Bond unico di ripiego',
          laggBond: 'Bond LACP (LAGG)',
          singlePort: 'Porta singola'
        },
        colAction: 'Azione',
        colKind: 'Tipo',
        colDevice: 'Device',
        colDetail: 'Dettaglio',
        action: { add: 'aggiungi', remove: 'rimuovi', replace: 'sostituisci' },
        diffEmpty: 'Nessuna differenza pendente — il documento applicato combacia col render.',
        diffBlocked: 'Nessun diff: i rifiuti qui sopra bloccano questo nodo.',
        phasePlanned: 'pianificato (passo 1 di 3)',
        phaseDraining: 'in drenaggio (passo 2 di 3)',
        probeUnobserved: 'Sonda di svuotamento: mai riportata — il vuoto è SCONOSCIUTO (un ritiro adesso verrebbe rifiutato).',
        probeFailed: 'Sonda di svuotamento: osservazione FALLITA — i timestamp noti si trascinano, mai un «vuoto» che nessuno ha misurato.',
        probeActive: plural({
          one: 'Sonda: {n} connessione attiva ancora sul vecchio indirizzo',
          many: 'Sonda: {n} di connessioni attive ancora sul vecchio indirizzo',
          other: 'Sonda: {n} connessioni attive ancora sul vecchio indirizzo'
        }),
        probeLastActive: 'ultima attività {time}',
        probeEmpty: plural({ one: 'Sonda: vuoto da {n} minuto', many: 'Sonda: vuoto da {n} di minuti', other: 'Sonda: vuoto da {n} minuti' }),
        probeEmptyUnknown: 'Sonda: vuoto (da quando non è noto).',
        impact: '{nics} vNIC e {apps} App su questo nodo usano queste reti: {list}',
        apply: 'Applica su questo nodo',
        applyTitle: 'Applica la derivazione — {node}',
        applyIntro:
          "La configurazione derivata riscrive il documento di rete di {node} ({n} modifiche) dalla stessa porta dell'editor host-network — checkpoint NetworkManager e sonda onesta compresi. Gli avvisi del piano qui sotto viaggiano con l'atto: leggili prima di confermare.",
        applyError: 'Apply non riuscito',
        applyRefused: 'Il nodo ha rifiutato:',
        planMoved: 'La fabric è cambiata dopo questa anteprima — NIENTE è stato applicato. Il piano è stato ricaricato: riguarda e riprova.',
        retire: 'Ritira',
        retireTitle: 'Punto di non ritorno — ritiro di {address}',
        restore: 'Ripristina',
        restoreTip: 'Torna allo snapshot pre-apply (generazione {previous}) — l\'undo a un passo (H13)',
        restoreTitle: 'Ripristina il documento precedente — {node}',
        restoreIntro: 'Il documento di rete di {node} torna allo snapshot scattato prima dell\'ultimo apply (generazione {previous}), riscritto come generazione {next} al posto della {current}. Il worker lo converge come qualunque altro documento.',
        restoreRedo: 'Il documento rimpiazzato diventa il nuovo snapshot: ripristinare di nuovo è il redo.',
        restoreClosesChain: 'Lo snapshot è il documento manuale pre-derivazione: la catena si chiude qui — nessun altro undo, il nodo torna manuale fino al prossimo apply.',
        restoreConfirm: 'Ripristina',
        restoreError: 'Ripristino non riuscito',
        retireIntro:
          'Questo rimuove {address} ({network}) da {point} definitivamente. Dopo questo atto la via del ritorno è il flip INVERSO — ri-dichiarare il vecchio indirizzo attraverso lo stesso engine — mai un ripristino cieco.',
        retireOld: 'Indirizzo vecchio (rimosso)',
        retireNew: 'Indirizzo nuovo (resta)',
        retireEmptyFact: "La sonda dà l'indirizzo vuoto: questo ritiro non tronca nessuna connessione osservata.",
        retireConfirm: "Ritira l'indirizzo",
        severActiveLoss: plural({
          one: 'Tronca {n} connessione attiva ancora sul vecchio indirizzo — muore con questo atto',
          many: 'Tronca {n} di connessioni attive ancora sul vecchio indirizzo — muoiono con questo atto',
          other: 'Tronca {n} connessioni attive ancora sul vecchio indirizzo — muoiono con questo atto'
        }),
        severUnknownLoss: "Accetta l'IGNOTO: la sonda non ha mai osservato questo indirizzo (o l'osservazione fallisce) — connessioni vive possono essere troncate."
      },
      overviewHint: "La fabric a colpo d'occhio: ogni rete logica con MTU e subnet (le VLAN vivono solo nelle mappature per-porta/per-LAGG — la stessa rete può viaggiare con tag diversi su porte diverse). La fabric è il registro di un'infrastruttura ESTERNA — TVirt osserva e dichiara, non fa mai provisioning, e tocca solo le NIC registrate sotto il suo controllo.",
      absorptionHint: 'Ogni subnet dichiarata qui DERIVA la sua voce nel registro IPAM nella stessa scrittura — vive e muore con la rete.',
      scopeFixedHint: 'Lo scope è fisso alla radice del cluster: l\'infrastruttura fisica è per definizione cluster-wide.',
      networksHint: 'Ogni rete porta una MTU obbligatoria (default 1500) e una subnet obbligatoria; gateway, server DNS e rotte statiche dichiarati qui sono la FONTE UNICA per NIC dei server, App e servizi di rete.',
      switchesHint: 'Il registro manuale: una porta è non usata, mappata (traffico untagged e per VLAN tag sulle reti logiche) o membro di un LAGG — un membro non porta mappature proprie.',
      mtu: 'MTU',
      cidr: 'CIDR',
      cidrHint: "Obbligatoria — l'IPAM ne deriva automaticamente la voce di registro",
      nodeAddressing: 'Assegnazione ai nodi',
      nodeAddressingNone: 'Nessuna — niente indirizzo per nodo',
      nodeAddressingPool: 'Pool — un indirizzo sticky per ogni nodo consegnato',
      nodeAddressingHint: 'Con Pool la derivazione fabric assegna a ogni nodo consegnato un indirizzo dai range riservati a TVirt (sticky una volta assegnato).',
      backing: 'Backing',
      gateway: 'Gateway',
      ranges: 'Range riservati',
      dns: 'Server DNS (separati da virgola)',
      dnsShort: 'DNS',
      singleSourceHint: 'Gateway, DNS e rotte dichiarati qui sono la fonte unica — NIC dei server, App e servizi di rete attingono da qui',
      routesTitle: 'Rotte statiche',
      routeDestination: 'Destinazione (CIDR)',
      routeVia: 'Via (next hop)',
      portUsed: 'Usata',
      vlanMappings: 'Mapping VLAN',
      vlanTag: 'Tag VLAN',
      vlanNetwork: 'Rete logica',
      nameTaken: 'Nome già usato nella fabric',
      addNetwork: 'Aggiungi rete',
      editNetwork: 'Modifica rete',
      deleteNetwork: 'Rimuovi rete',
      deleteNetworkConfirm: "Rimuovere la rete logica {name} dalla fabric? La sua subnet derivata nell'IPAM muore con lei (rifiutato finché un servizio o un'App la usa). Le mappature su porte e LAGG che la puntano vengono ripulite.",
      switchName: 'Switch',
      portName: 'Porta',
      positionHint: 'Posizione fisica (opzionale) — lo stesso vocabolario del posizionamento server; solo dichiarativa',
      addSwitch: 'Aggiungi switch',
      editSwitch: 'Modifica switch',
      deleteSwitch: 'Rimuovi switch',
      deleteSwitchConfirm: 'Rimuovere lo switch {name} con tutte le sue porte? I LAGG si restringono di conseguenza (e muoiono sotto le due porte).',
      addPort: 'Aggiungi porta',
      editPort: 'Modifica porta',
      deletePort: 'Rimuovi porta',
      deletePortConfirm: 'Rimuovere la porta {name}?',
      untagged: 'Untagged',
      tagged: 'Tagged (tag:rete)',
      taggedShort: 'Tagged',
      portCabling: 'Cablaggio',
      portState: 'Stato',
      stateUnused: 'non usata',
      stateMapped: 'mappata',
      laggPortGuard: 'Una porta membro di un LAGG si modifica dal lato LAGG',
      laggsTitle: 'LAGG',
      laggsHint: 'Un LAGG è 2+ porte in LACP e può attraversare gli switch (MLAG, trasparente); entrano solo porte non usate, il LAGG è la superficie di mappatura, una porta rimossa torna non usata.',
      addLagg: 'Aggiungi LAGG',
      editLagg: 'Modifica LAGG',
      deleteLagg: 'Rimuovi LAGG',
      deleteLaggConfirm: 'Rimuovere il LAGG {name}? Le sue porte membri tornano non usate.',
      lacpRate: 'Rate LACP',
      laggPorts: 'Porte membri (2+)',
      laggPortsHint: 'Entrano solo porte non usate — libera prima una porta mappata',
      noNetworks: 'Nessuna rete logica dichiarata.',
      noSwitches: 'Nessuno switch dichiarato.',
      noLaggs: 'Nessun LAGG dichiarato.',
      staleConflict: 'Un altro operatore ha modificato questa fabric mentre la stavi cambiando: il salvataggio è stato rifiutato per non cancellare il suo lavoro. Le tue modifiche sono ancora qui, intatte. La fabric aggiornata è già stata ricaricata: conferma di nuovo per riapplicarle sopra, oppure annulla e guarda che cosa è cambiato.'
    },
    ipam: {
      title: 'IPAM',
      derivedHint: "Solo derivato: ogni subnet nasce da una rete logica di fabric o da una overlay — si modifica alla sorgente. Gli indirizzi assegnati mostrano CHI li tiene; i range riservati a TVirt sono i pool dell'assegnazione automatica.",
      search: 'Cerca per nome, CIDR o indirizzo',
      empty: "Nessuna rete — dichiara una rete logica di fabric o una subnet overlay: l'IPAM deriva da loro automaticamente.",
      reserved: 'Range riservati a TVirt',
      address: 'Indirizzo',
      origin: 'Provenienza',
      origin_declared: 'dichiarato',
      origin_observed: 'osservato',
      holder: 'Tenuto da',
      noAddresses: 'Nessun indirizzo noto in questa subnet.',
      sourceFabric: 'Fabric',
      sourceOverlay: 'Overlay',
      editAtSource: 'Modifica alla sorgente'
    },

    // Rete > Firewall (#168): il vocabolario dei gruppi + la terna per-NIC.
    firewall: {
      title: 'Gruppi firewall',
      createTitle: 'Nuovo gruppo firewall',
      editTitle: 'Modifica gruppo firewall',
      empty: 'Nessun gruppo firewall — creane uno: un vocabolario di regole riusabile, riferito dalle NIC guest per nome.',
      nameHint: 'Lettere, cifre e . - _ interni; unico dentro il suo scope (gli omonimi altrove si distinguono con /scope/nome)',
      description: 'Descrizione',
      descriptionHint: 'Una riga di intento, mostrata ovunque il gruppo si scelga',
      enforced: 'Imposto allo scope',
      enforcedHint: 'Spento: vocabolario puro — le regole legano solo le NIC che riferiscono il gruppo. Acceso: le regole legano ogni NIC guest col firewall acceso sotto lo scope del gruppo, riferite o no.',
      enforcedChip: 'Imposto',
      enforcedChipHint: 'Le regole legano ogni NIC guest col firewall acceso sotto lo scope del gruppo, riferite o no',
      enforceWarning: 'Attivando l\'imposizione le regole legano OGNI NIC guest col firewall acceso sotto {scope} al prossimo allineamento, anche senza riferimento esplicito — e dentro una chain accesa il drop di un antenato è definitivo: i livelli sotto non lo riaprono.',
      unenforceInfo: 'Togliendo l\'imposizione le regole restano solo sulle NIC che riferiscono il gruppo esplicitamente.',
      rules: 'Regole',
      tabOverview: 'Panoramica',
      tabRules: 'Regole',
      orderHint: 'L\'ordine delle righe È l\'ordine di valutazione dentro il gruppo: vince la prima regola che combacia.',
      statefulHint: 'Lo stateful è implicito: le risposte al traffico ammesso passano da sole (established/related accettate, invalid scartato — le inietta il motore, non si scrivono a mano).',
      noRules: 'Nessuna regola — un vocabolario in costruzione: riferirlo è lecito e non rende nulla.',
      addRule: 'Aggiungi regola',
      direction: 'Direzione',
      directionIn: 'In ingresso',
      directionOut: 'In uscita',
      action: 'Azione',
      action_accept: 'Accetta',
      action_drop: 'Scarta',
      action_reject: 'Rifiuta',
      protocol: 'Protocollo',
      protocolAny: 'Qualsiasi',
      ports: 'Porte',
      source: 'Sorgente',
      dest: 'Destinazione',
      comment: 'Commento',
      addressHint: 'CIDR IPv4 oppure <fabric>/<rete logica>; vuoto = qualsiasi',
      moveUp: 'Sposta su',
      moveDown: 'Sposta giù',
      nicFirewall: 'Firewall',
      nicAntiSpoof: 'Anti-spoofing',
      nicGroups: 'Gruppi firewall',
      nicGroupsPlaceholder: 'Scegli un gruppo…',
      nicGroupsHint: 'I gruppi visibili, compresi quelli degli scope superiori (utilizzabili verso il basso, come i tag)',
      nicGroupCount: { one: '{n} gruppo firewall', many: '{n} gruppi firewall', other: '{n} gruppi firewall' },
      nicOff: 'Firewall spento',
      pendingRestartHint: 'Le modifiche firewall seguono il modello salvato/applicato: attendono il prossimo ciclo di alimentazione (PendingRestart). I gruppi imposti dagli scope superiori si applicano quando il firewall della NIC è acceso.',
      appConvergeHint: 'Le modifiche firewall si applicano al prossimo allineamento del container. I gruppi imposti dagli scope superiori si applicano quando il firewall è acceso.',
      enforcedAncestors: 'Imposti dagli scope superiori',
      enforcedAncestorsHint: 'Questo gruppo si applica a ogni NIC sotto il suo scope col firewall ACCESO — il toggle è l\'interruttore generale della NIC (spento = nessuna chain, antenati compresi); dentro una chain accesa l\'autorità dell\'antenato è definitiva: il suo drop non si riapre sotto.'
    }
  }
}
